import React, {
  useCallback,
  useEffect,
  useState,
  createContext,
  useContext,
} from 'react';
import { window } from 'browser-monads';
import UnoptimizedClient from 'shopify-buy/index.unoptimized.umd';
export const CHECKOUT_ID = 'kdh_checkout_id';

const ctxDefaultValue = {
  checkout: null,
  setCheckout: () => {},
  client: null,
  setClient: () => {},
  products: null,
  setProducts: () => {},
  loading: false,
  setLoading: () => {},
  cartDrawerOpen: false,
  setCartDrawerOpen: () => {},
};

const ShopContext = createContext(ctxDefaultValue);

export const useProducts = () => {
  const { products } = useContext(ShopContext);
  return products;
};

export const useCheckout = () => {
  const { checkout } = useContext(ShopContext);
  return checkout;
};

export function useCart() {
  const { checkout, setCheckout, client } = useContext(ShopContext);

  const addToCart = async (items) => {
    const check = await client.checkout.addLineItems(checkout.id, items);
    setCheckout(check);
  };

  const emptyCart = async () => {
    const check = await client.checkout.removeLineItems(
      checkout.id,
      checkout.lineItems.map((item) => item.id)
    );
    setCheckout(check);
  };

  const updateNote = async (note) => {
    const attributes = {
      note,
    };

    const check = await client.checkout.updateAttributes(
      checkout.id,
      attributes
    );
    setCheckout(check);
  };

  return { addToCart, emptyCart, updateNote, checkout };
}

export function useAddToCart() {
  const { checkout, setCheckout, client } = useContext(ShopContext);

  const addToCart = async (items) => {
    const check = await client.checkout.addLineItems(checkout.id, items);
    setCheckout(check);
  };

  return addToCart;
}

export function useCartDrawer() {
  const { cartDrawerOpen, setCartDrawerOpen } = useContext(ShopContext);
  return { cartDrawerOpen, setCartDrawerOpen };
}

const ShopProvider = ({ children }) => {
  const [loading, setLoading] = useState(ctxDefaultValue.loading);
  const [checkout, setCheckout] = useState(ctxDefaultValue.checkout);
  const [products, setProducts] = useState(ctxDefaultValue.products);
  const [client, setClient] = useState(ctxDefaultValue.client);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(
    ctxDefaultValue.cartDrawerOpen
  );

  // TODO: put in env vars
  const initializeShopifyClient = () => {
    const c = UnoptimizedClient.buildClient({
      storefrontAccessToken: '9f6b0dc604f788c10d2ec45aeaca3034',
      domain: 'kyro-distillery-home.myshopify.com',
    });
    setClient(c);
  };

  const initializeProducts = async () => {
    const productMetafields = [
      {
        key: 'kyro_booking',
        namespace: 'kyro_booking',
      },
    ];

    const productFields = (product) => {
      product.add('id');
      product.add('availableForSale');
      product.add('createdAt');
      product.add('updatedAt');
      product.add('descriptionHtml');
      product.add('description');
      product.add('handle');
      product.add('productType');
      product.add('title');
      product.add('vendor');
      product.add('publishedAt');
      product.add('onlineStoreUrl');
      product.add('options', (opts) => {
        opts.add('name');
        opts.add('values');
      });
      product.add('tags');
      product.add(
        'metafields',
        { args: { identifiers: productMetafields } },
        (meta) => {
          meta.add('description');
          meta.add('id');
          meta.add('key');
          meta.add('namespace');
          meta.add('value');
          meta.add('type');
        }
      );
      product.addConnection('images', { args: { first: 50 } }, (image) => {
        image.add('id');
        image.add('src');
        image.add('altText');
      });
      product.addConnection('variants', { args: { first: 50 } }, (variant) => {
        variant.add('id');
        variant.add('title');
        variant.add('price', (opts) => {
          opts.add('amount');
          opts.add('currencyCode');
        });
        variant.add('weight');
        variant.add('availableForSale');
        variant.add('sku');
        variant.add('compareAtPrice', (opts) => {
          opts.add('amount');
          opts.add('currencyCode');
        });
        variant.add('image', (opts) => {
          opts.add('id');
          opts.add('src');
          opts.add('altText');
        });
        variant.add('selectedOptions', (opts) => {
          opts.add('name');
          opts.add('value');
        });
      });
    };
    const query = client.graphQLClient.query(async (root) => {
      root.addConnection('products', { args: { first: 50 } }, (product) => {
        productFields(product);
      });
    });

    const data = await client.graphQLClient.send(query);
    const bookingProducts = data.data.products.edges
      .filter((e) => e.node.metafields.length)
      .map((e) => {
        return {
          ...e.node,
          metafield: e.node.metafields[0]
            ? JSON.parse(e.node.metafields[0].value)
            : null,
        };
      });

    setProducts(bookingProducts);

    setLoading(false);
  };

  const createCheckout = async () => {
    const ch = await client.checkout.create();
    setCheckout(ch);
    window.localStorage.setItem(CHECKOUT_ID, ch.id);
    return;
  };

  const initializeCheckout = useCallback(async () => {
    try {
      const existingCheckoutId = window.localStorage.getItem(CHECKOUT_ID);

      if (existingCheckoutId) {
        const ch = await client.checkout.fetch(existingCheckoutId);
        // Make sure this cart hasn’t already been purchased
        if (!ch || ch.completedAt) {
          await createCheckout();
        } else {
          setCheckout(ch);
        }
        return initializeProducts();
      }
      return createCheckout();
    } catch (e) {
      // TODO: Handle errors
      console.error('Checkout error: ', e);
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (client) {
      initializeCheckout();
    }
  }, [client]);

  useEffect(() => {
    if (!loading && !checkout && !client) {
      setLoading(true);
      initializeShopifyClient();
    }
  }, [loading, checkout, client]);

  return (
    <ShopContext.Provider
      value={{
        loading,
        setLoading,
        checkout,
        setCheckout,
        products,
        setProducts,
        client,
        setClient,
        cartDrawerOpen,
        setCartDrawerOpen,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export default ShopProvider;
